import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../lib/apiClient";
import { useCurrentAppUser } from "../lib/useCurrentAppUser";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import { PeopleTable } from "../components/PeopleTable";
import type { PersonSummary, ConnectionRecord } from "@shared/types";

type Tab = "directory" | "requests";

/**
 * Org-wide people directory + connection requests — not scoped to a
 * project, unlike the rest of the app's Field pages (see ProjectContext).
 */
export function NetworkPage() {
  const navigate = useNavigate();
  const { user: me } = useCurrentAppUser();
  const [tab, setTab] = useState<Tab>("directory");
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState<PersonSummary[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(true);
  const [sent, setSent] = useState<ConnectionRecord[]>([]);
  const [received, setReceived] = useState<ConnectionRecord[]>([]);
  const [connections, setConnections] = useState<ConnectionRecord[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function loadPeople(q: string) {
    setPeopleLoading(true);
    api
      .get<{ people: PersonSummary[] }>(`/api/people${q ? `?query=${encodeURIComponent(q)}` : ""}`)
      .then((data) => setPeople(data.people))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load the directory."))
      .finally(() => setPeopleLoading(false));
  }

  function loadRequests() {
    setRequestsLoading(true);
    api
      .get<{ sent: ConnectionRecord[]; received: ConnectionRecord[]; connections: ConnectionRecord[] }>("/api/connections")
      .then((data) => {
        setSent(data.sent);
        setReceived(data.received);
        setConnections(data.connections);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load your connections."))
      .finally(() => setRequestsLoading(false));
  }

  useEffect(() => {
    loadPeople("");
    loadRequests();
  }, []);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    loadPeople(query);
  }

  async function connect(person: PersonSummary) {
    setBusyId(person.id);
    setError(null);
    try {
      await api.post("/api/connections", { recipientId: person.id });
      loadPeople(query);
      loadRequests();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to send connection request.");
    } finally {
      setBusyId(null);
    }
  }

  async function respond(connectionId: string, action: "accept" | "decline" | "cancel") {
    setBusyId(connectionId);
    setError(null);
    try {
      await api.post(`/api/connections/${connectionId}/respond`, { action });
      loadPeople(query);
      loadRequests();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to update this request.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="content-col-wide">
      <p className="sources-desc" style={{ marginTop: 0 }}>
        Find and connect with Resident Engineers and Inspectors across the org. Full profiles unlock once you're connected.
      </p>
      {error && <p className="error-text">{error}</p>}

      <div className="tab-bar">
        <button className={tab === "directory" ? "tab-btn active" : "tab-btn"} onClick={() => setTab("directory")}>
          Directory
        </button>
        <button className={tab === "requests" ? "tab-btn active" : "tab-btn"} onClick={() => setTab("requests")}>
          Requests
          {received.length > 0 && <span className="tab-badge">{received.length}</span>}
        </button>
      </div>

      {tab === "directory" && (
        <>
          <form className="network-search" onSubmit={handleSearch}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, or title…"
            />
            <button className="btn" type="submit">
              Search
            </button>
          </form>
          {peopleLoading ? (
            <p className="muted">Loading…</p>
          ) : (
            <PeopleTable people={people} busyId={busyId} onConnect={connect} emptyMessage="No one found." />
          )}
        </>
      )}

      {tab === "requests" && (
        <>
          {requestsLoading ? (
            <p className="muted">Loading…</p>
          ) : (
            <>
              {received.length > 0 && (
                <div className="card">
                  <h2 className="settings-section-title">Received</h2>
                  {received.map((c) => (
                    <div className="network-request-row" key={c.id}>
                      <button className="link-btn" onClick={() => navigate(`/network/${c.requesterId}`)}>
                        {c.requesterEmail}
                      </button>
                      <span className="muted" style={{ fontSize: "0.8rem" }}>
                        {formatRelativeTime(c.createdAt)}
                      </span>
                      <div className="doc-actions">
                        <button className="btn btn-primary" disabled={busyId === c.id} onClick={() => respond(c.id, "accept")}>
                          Accept
                        </button>
                        <button className="btn btn-danger" disabled={busyId === c.id} onClick={() => respond(c.id, "decline")}>
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {sent.length > 0 && (
                <div className="card">
                  <h2 className="settings-section-title">Sent</h2>
                  {sent.map((c) => (
                    <div className="network-request-row" key={c.id}>
                      <button className="link-btn" onClick={() => navigate(`/network/${c.recipientId}`)}>
                        {c.recipientEmail}
                      </button>
                      <span className="muted" style={{ fontSize: "0.8rem" }}>
                        {formatRelativeTime(c.createdAt)}
                      </span>
                      <div className="doc-actions">
                        <button className="btn" disabled={busyId === c.id} onClick={() => respond(c.id, "cancel")}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="card">
                <h2 className="settings-section-title">Your connections</h2>
                {connections.length === 0 ? (
                  <p className="muted">No connections yet.</p>
                ) : (
                  connections.map((c) => {
                    const isMeRequester = c.requesterId === me?.id;
                    const otherId = isMeRequester ? c.recipientId : c.requesterId;
                    const otherEmail = isMeRequester ? c.recipientEmail : c.requesterEmail;
                    return (
                      <div className="network-request-row" key={c.id}>
                        <button className="link-btn" onClick={() => navigate(`/network/${otherId}`)}>
                          {otherEmail}
                        </button>
                        <span className="muted" style={{ fontSize: "0.8rem" }}>
                          Connected {formatRelativeTime(c.respondedAt ?? c.createdAt)}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
